import { delay, filter, map, of, switchMap, take, tap } from 'rxjs';

import type { ElementRef } from '@angular/core';
import type { AbstractControl, FormGroup, NgForm } from '@angular/forms';
import type { Undefinable } from '@kp/types';
import type { Observable } from 'rxjs';

type ValidateFormConfig = {
  checkDirty?: boolean;
  filterInvalid?: boolean;
  formEl?: ElementRef<HTMLFormElement> | HTMLFormElement | undefined;
  ignoreForm?: boolean;
};

function isFormGroup(group: AbstractControl): group is FormGroup {
  return !!(group as FormGroup).controls;
}

function parseControls(ctrls: Record<string, AbstractControl>): void {
  for (const ctrl of Object.values(ctrls)) {
    ctrl.markAllAsTouched();
    ctrl.updateValueAndValidity();

    if (isFormGroup(ctrl)) {
      parseControls(ctrl.controls);
    }
  }
}

function scrollInvalidToView(form?: HTMLFormElement): void {
  if (!form) {
    return;
  }

  const invalidElements = form.querySelectorAll('.ng-invalid');
  const element = [...invalidElements].find(
    el =>
      el.querySelectorAll('.ng-invalid').length === 0 ||
      el.matches('input, select, textarea'),
  );

  if (!element) {
    return;
  }

  window.scroll({
    behavior: 'smooth',
    left: 0,
    top: element.getBoundingClientRect().top + window.scrollY - 50,
  });
}

/**
 * Returns observable of form states.
 *
 * By default it filters out **INVALID** state so it won't continue
 * Config enables you to modify validation message and whether it will filter out invalid states of the form or not.
 *
 * ## Example
 *
 * To scroll to invalid field add `formEl` to the config:
 *
 * ```ts
 * @ViewChild('formEl') formEl?: ElementRef<HTMLFormElement>;
 *
 * submit(): void {
 *  validateForm$(form, { formEl: this.formEl })
 * }
 * ```
 * @throws
 */
// oxlint-disable-next-line max-lines-per-function
function validateForm$(
  formGroup?: NgForm,
  config?: Undefinable<ValidateFormConfig>,
): Observable<'INVALID' | 'VALID'> {
  if (!formGroup) {
    if (config?.ignoreForm) {
      return of('VALID');
    }

    throw new Error('no form present');
  }

  const {
    checkDirty,
    filterInvalid: filterInvalidCfg,
    formEl,
  } = { checkDirty: false, filterInvalid: true, ...config };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return of(true).pipe(
    tap(() => {
      (
        (document as Document | undefined)?.activeElement as
          | HTMLElement
          | undefined
      )?.blur();
    }),
    delay(0),
    tap(() => {
      parseControls(formGroup.controls);
    }),
    switchMap(() =>
      formGroup.status === 'PENDING'
        ? formGroup.form.statusChanges.pipe(
            filter(status => status !== 'PENDING'),
            take(1),
          )
        : of(formGroup.valid),
    ),
    delay(200),
    map(() => {
      if (checkDirty && !formGroup.dirty) {
        return 'INVALID';
      }

      return formGroup.valid ? 'VALID' : 'INVALID';
    }),
    delay(0),
    tap(validation => {
      if (validation !== 'VALID' && formEl) {
        scrollInvalidToView(
          'nativeElement' in formEl ? formEl.nativeElement : formEl,
        );
      }
    }),
    filter(valid => (filterInvalidCfg ? valid === 'VALID' : true)),
    take(1),
  ) as Observable<'INVALID' | 'VALID'>;
}

export { validateForm$ };
